import { useState, useContext, useEffect, useMemo } from 'react'
import WizardDataContext from '../../../store/WizardDataContext';

import { FormGroup, Checkbox, FormControlLabel, Input } from "@mui/material";
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import NewTermImg from '../../../assets/img/ad/new-term.svg'

// Memoized constant array - moved outside component to prevent recreation on every render
const DEFAULT_TERM_ARR = [
  { id: 1, instruction: 'Applicable to new customers only', checked: false },
  { id: 2, instruction: 'Applicable to all customers', checked: false },
  { id: 3, instruction: 'Applicable to students only', checked: false },
  { id: 4, instruction: 'Applicable to pensioners only', checked: false },
  { id: 5, instruction: 'NOT applicable to already discounted products and services', checked: false },
  { id: 6, instruction: 'Only available on your birthday', checked: false },
];

// Define mutually exclusive term groups
const CONFLICT_GROUPS = {
  'Applicable to all customers': [
    'Applicable to new customers only',
    'Applicable to students only',
    'Applicable to pensioners only'
  ],
  'Applicable to new customers only': [
    'Applicable to all customers'
  ],
  'Applicable to students only': [
    'Applicable to all customers',
    'Applicable to pensioners only'
  ],
  'Applicable to pensioners only': [
    'Applicable to all customers',
    'Applicable to students only'
  ]
};

export default function TermsAndConditions({ setDisableBtn }) {
  const wizardCtx = useContext(WizardDataContext);
  const { wizardData, updateData } = wizardCtx;
  const terms = wizardData.terms;

  const [allTerms, setAllTerms] = useState(DEFAULT_TERM_ARR);
  const [newTerm, setNewTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [maxLimit, setMaxLimit] = useState(false);

  const style = { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 600, bgcolor: 'background.paper', border: '2px solid #000', boxShadow: 24, p: 4, borderRadius: '5px'};

  useEffect(() => {
    // Check if at least one term is selected
    const hasSelectedTerms = terms.terms && terms.terms.length > 0;
    setDisableBtn(!hasSelectedTerms);
  }, [terms.terms, setDisableBtn])

  // Restore terms selection from context when component mounts
  useEffect(() => {
    // Start with the initial terms - use memoized constant
    let updatedTerms = [...DEFAULT_TERM_ARR];
    
    // Add custom terms from context if they exist
    if (terms.customTerms) {
      updatedTerms = [...updatedTerms, ...terms.customTerms];
    }
    
    // Update checked state based on context
    updatedTerms.forEach(term => {
      const isSelected = terms.terms && terms.terms.includes(term.instruction);
      term.checked = isSelected;
    });
    
    setAllTerms(updatedTerms);
  }, [terms.terms, terms.customTerms]);

  const handleInputChange = (value, setValue, setLimit) => {
    setValue(value);
    setLimit(value.length > 100);
  };

  const handleAddItem = (newItem, setNewItem, allItems, setAllItems, setOpen) => {
    if (newItem && newItem.length <= 100) {
      const newId = Math.max(...allItems.map(item => item.id)) + 1;
      const newItemObj = { id: newId, instruction: newItem, checked: true };
      allItems.push(newItemObj);
      
      // Store custom term in context
      const currentCustomTerms = terms.customTerms || [];
      const updatedCustomTerms = [...currentCustomTerms, newItemObj];
      
      const currentItems = terms.terms || [];
      updateData('terms', { 
        terms: [...currentItems, newItem],
        customTerms: updatedCustomTerms
      });
    }
    setNewItem("");
    setOpen(false);
  };

  const handleDeleteItem = (index, allItems, setAllItems) => {
    const itemToDelete = allItems[index];
    
    // Only allow deletion of custom terms (id > 6), not default ones
    if (itemToDelete && itemToDelete.id > 6) {
      setAllItems((prev) => prev.filter((_, i) => i !== index));
      
      // Remove from context as well
      removeItem(itemToDelete.instruction);
      
      // Remove from custom terms in context
      const currentCustomTerms = terms.customTerms || [];
      const updatedCustomTerms = currentCustomTerms.filter(item => item.id !== itemToDelete.id);
      
      updateData('terms', { 
        customTerms: updatedCustomTerms
      });
    }
  };

  const handleSelectionChange = (e, item, allItems, setAllItems) => {
    if (e.target.checked) {
      const currentItems = terms.terms || [];
      
      // Check for conflicts before adding
      const conflictingTerms = CONFLICT_GROUPS[item.instruction] || [];
      const hasConflict = conflictingTerms.some(conflictTerm => 
        currentItems.includes(conflictTerm)
      );
      
      if (hasConflict) {
        // Remove conflicting terms
        const updatedItems = currentItems.filter(term => 
          !conflictingTerms.includes(term)
        );
        
        // Update context with new selection and removed conflicts
        updateData('terms', { terms: [...updatedItems, item.instruction] });
        
        // Update local state to reflect removed conflicts
        setAllItems(prev => prev.map(term => {
          if (term.id === item.id) {
            return { ...term, checked: true };
          }
          if (conflictingTerms.includes(term.instruction)) {
            return { ...term, checked: false };
          }
          return term;
        }));
      } else {
        // No conflicts, proceed normally
        updateData('terms', { terms: [...currentItems, item.instruction] });
        setAllItems(prev => prev.map(term => 
          term.id === item.id ? { ...term, checked: true } : term
        ));
      }
    } else {
      // Only remove from context, don't remove from local state
      removeItem(item.instruction);
      setAllItems(prev => prev.map(term => 
        term.id === item.id ? { ...term, checked: false } : term
      ));
    }
  };

  const removeItem = (label) => {
    const currentItems = terms.terms || [];
    const index = currentItems.indexOf(label);
    if (index !== -1) {
      const tempArray = currentItems.slice();
      tempArray.splice(index, 1);
      updateData('terms', { terms: tempArray });
    }
  };

  const isItemSelected = (item) => {
    const items = terms.terms || [];
    return items.indexOf(item.instruction) !== -1;
  };

  return (
    <>
      <div className="step6-container terms-step6-container">
        <div className="step6">
          <div className="heading">
            <h1>Terms and Conditions</h1>
            <h3>Select or add the terms applicable to your promotional offer</h3>
          </div>
          <FormGroup className="select-terms">
            {allTerms && allTerms.length > 0 ? (
              allTerms.map((item, i) => (
                <div key={item.id} className="term">
                  <FormControlLabel 
                    onClick={(e) => handleSelectionChange(e, item, allTerms, setAllTerms)} 
                    control={<Checkbox checked={isItemSelected(item)} />} 
                    label={item.instruction} 
                  /> 
                  {/* Only show X button for custom terms (id > 6) */}
                  {item.id > 6 && (
                    <CloseRoundedIcon 
                      className="close" 
                      onClick={() => handleDeleteItem(i, allTerms, setAllTerms)} 
                    />
                  )}
                </div>
              ))
            ) : (
              <div style={{ textAlign: 'center', padding: '20px', color: '#666', fontStyle: 'italic',backgroundColor: '#f5f5f5',borderRadius: '8px',margin: '10px 0'}}>
                No terms and conditions available. Click "Create New One" to add some.
              </div>
            )}
          </FormGroup>
          <div className="create-new" onClick={() => setOpen(true)}>
            <AddRoundedIcon /> <span>CREATE NEW ONE</span>
          </div>
        </div>

        <Modal
          open={open}
          onClose={() => setOpen(false)}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={style}>
            <div className="new-term-container">
              <div className="new-term">
                <div className="heading">
                  <h1>Add a new term or condition</h1>
                  <h3>Enter just 1 term or condition at a time</h3>
                </div>
                <div className="image">
                  <img src={NewTermImg} width={200} height={200} alt="new term" />
                </div>
                <div className="input">
                  <Input 
                    value={newTerm} 
                    placeholder="Ex: Deliva reserves the right to discontinue a coupon at any time" 
                    onChange={(e) => handleInputChange(e.target.value, setNewTerm, setMaxLimit)} 
                  />
                  <span className={maxLimit ? "helper-text warning" : "helper-text"}>MAX 100 CHARACTERS</span>
                </div>
                <div className="blue-btn">
                  <Button onClick={() => handleAddItem(newTerm, setNewTerm, allTerms, setAllTerms, setOpen)} disabled={maxLimit} style={{ marginTop: '10px' }}>
                    <AddRoundedIcon /> <span>ADD</span>
                  </Button>
                </div>
                <div className="btn-close">
                  <CloseRoundedIcon onClick={() => setOpen(false)} />
                </div>
              </div>
            </div>
          </Box>
        </Modal>
      </div>
    </>
  )
}
